import * as React from 'react'
import { ModalUnstyled, ModalUnstyledOwnProps } from './ModalUnstyled'
import { resolveComponentProps } from '../utils'
import { Backdrop } from '../Backdrop'
import clsx from 'clsx'
import style from './index.module.scss'
import { useCurrentTheme } from '../ThemeProvider'

export type ModalProps = ModalUnstyledOwnProps

/**
 * Modal is a lower-level construct that is leveraged by the following components:
 *
 * - [Dialog](/material-ui/api/dialog/)
 * - [Drawer](/material-ui/api/drawer/)
 * - [Menu](/material-ui/api/menu/)
 * - [Popover](/material-ui/api/popover/)
 *
 * If you are creating a modal dialog, you probably want to use the [Dialog](/material-ui/api/dialog/) component
 * rather than directly using Modal.
 *
 * This component shares many concepts with [react-overlays](https://react-bootstrap.github.io/react-overlays/#modals).
 */

export const Modal = React.forwardRef<HTMLDivElement, ModalProps>(
  function Modal(props, ref) {
    const {
      closeAfterTransition = false,
      children,
      disableAutoFocus = false,
      disableEnforceFocus = false,
      disableEscapeKeyDown = false,
      disablePortal = false,
      disableRestoreFocus = false,
      disableScrollLock = false,
      hideBackdrop = false,
      keepMounted = false,
      slotProps,
      slots,
      classes,
      ...other
    } = props

    const [exited, setExited] = React.useState(true)

    const commonProps = {
      closeAfterTransition,
      disableAutoFocus,
      disableEnforceFocus,
      disableEscapeKeyDown,
      disablePortal,
      disableRestoreFocus,
      disableScrollLock,
      hideBackdrop,
      keepMounted,
    }

    const ownerState = {
      ...props,
      ...commonProps,
      exited,
    }

    const RootSlot = slots?.root ?? 'div'
    const BackdropSlot = slots?.backdrop ?? Backdrop

    const rootSlotProps = slotProps?.root
    const backdropSlotProps = slotProps?.backdrop

    const themeClassName = useCurrentTheme()

    return (
      <ModalUnstyled
        slots={{
          root: RootSlot,
          backdrop: BackdropSlot,
        }}
        slotProps={{
          root: () => ({
            ...resolveComponentProps(rootSlotProps, ownerState),
          }),
          backdrop: () => ({
            ...resolveComponentProps(backdropSlotProps, ownerState),
          }),
        }}
        onTransitionEnter={() => setExited(false)}
        onTransitionExited={() => setExited(true)}
        ref={ref}
        {...other}
        classes={{
          ...classes,
          root: clsx(style.root, classes?.root, themeClassName),
          backdrop: clsx(style.backdrop, classes?.backdrop),
          hidden: clsx(style.hidden, classes?.hidden),
        }}
        {...commonProps}
      >
        {children}
      </ModalUnstyled>
    )
  }
)
